import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { FaFilter, FaSearch, FaStar, FaMapMarkerAlt } from 'react-icons/fa';
import DistanceFilter from '../components/DistanceFilter';

export default function Rental() {
  const { t, i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    location: 'all',
    shop: 'all',
    priceMin: '',
    priceMax: '',
    available: true
  });
  const [centerCity, setCenterCity] = useState('');
  const [radiusKm, setRadiusKm] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchLocations();
    fetchShops();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [filters.category, filters.location, filters.shop, filters.search, centerCity, radiusKm]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops`);
      setShops(response.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      let url = `${API}/products`;
      const params = [];
      if (filters.category !== 'all') params.push(`category_id=${filters.category}`);
      if (filters.location !== 'all' && !centerCity) params.push(`location=${filters.location}`);
      if (filters.shop !== 'all') params.push(`shop_id=${filters.shop}`);
      if (filters.search) params.push(`search=${filters.search}`);
      if (centerCity && radiusKm) {
        params.push(`center_city=${encodeURIComponent(centerCity)}`);
        params.push(`radius_km=${radiusKm}`);
      }
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      
      const response = await axios.get(url);
      
      // Filter only rental products that are approved (active status)
      const rentalProducts = response.data.filter(p => 
        p.price_rental && p.stock_rental > 0 && p.status === 'active'
      );
      setProducts(rentalProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    // Price filters (client-side)
    if (filters.priceMin && product.price_rental < parseFloat(filters.priceMin)) {
      return false;
    }
    if (filters.priceMax && product.price_rental > parseFloat(filters.priceMax)) {
      return false;
    }
    
    // Availability filter (client-side)
    if (filters.available && product.stock_rental === 0) {
      return false;
    }
    
    return true;
  });

  // Group products by category
  const groupedProducts = categories.map(category => ({
    category,
    products: filteredProducts.filter(p => p.category_id === category.id)
  })).filter(group => group.products.length > 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-[#0A2540]">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="rental-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#0A2540] to-[#006B8F] text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="rental-title">
            {i18n.language === 'fr' ? 'Catalogue de Location' : 'Rental Catalog'}
          </h1>
          <p className="text-lg sm:text-xl opacity-90">
            {i18n.language === 'fr' ? "Louez l'équipement professionnel dont vous avez besoin" : 'Rent the professional equipment you need'}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filters Section */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <FaFilter className="text-[#0A2540]" />
              <h2 className="text-xl font-semibold text-[#0A2540]">{t('filter')}</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder={t('search')}
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="pl-10"
                />
              </div>

              {/* Category */}
              <Select value={filters.category || "all"} onValueChange={(value) => setFilters({...filters, category: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les catégories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {i18n.language === 'fr' ? cat.name_fr : cat.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Location */}
              <Select value={filters.location} onValueChange={(value) => {
                setFilters({...filters, location: value});
                if (value !== 'all') {
                  setCenterCity('');
                  setRadiusKm(null);
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les localisations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les localisations</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc} value={loc}>
                      {loc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Shop/Supplier */}
              <Select value={filters.shop} onValueChange={(value) => setFilters({...filters, shop: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous les fournisseurs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les fournisseurs</SelectItem>
                  {shops.map(shop => (
                    <SelectItem key={shop.id} value={shop.id}>
                      {shop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Price Range */}
              <Input
                type="number"
                placeholder="Prix min (€/jour)"
                value={filters.priceMin}
                onChange={(e) => setFilters({...filters, priceMin: e.target.value})}
              />
              <Input
                type="number"
                placeholder="Prix max (€/jour)"
                value={filters.priceMax}
                onChange={(e) => setFilters({...filters, priceMax: e.target.value})}
              />
            </div>

            {/* Distance Filter */}
            <div className="mt-4">
              <DistanceFilter
                centerCity={centerCity}
                setCenterCity={setCenterCity}
                radiusKm={radiusKm}
                setRadiusKm={setRadiusKm}
                onClear={() => {
                  setCenterCity('');
                  setRadiusKm(null);
                  setFilters({...filters, location: 'all'});
                }}
              />
            </div>

            <div className="mt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="available"
                  checked={filters.available}
                  onChange={(e) => setFilters({...filters, available: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="available" className="text-sm">
                  Afficher uniquement les équipements disponibles
                </label>
              </div>
              
              <Button
                onClick={() => {
                  setFilters({
                    search: '',
                    category: 'all',
                    location: 'all',
                    shop: 'all',
                    priceMin: '',
                    priceMax: '',
                    available: true
                  });
                  setCenterCity('');
                  setRadiusKm(null);
                }}
                variant="outline"
                size="sm"
              >
                Réinitialiser les filtres
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Products by Category */}
        {groupedProducts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-500">Aucun équipement de location trouvé</p>
            </CardContent>
          </Card>
        ) : (
          groupedProducts.map(({ category, products }) => (
            <div key={category.id} className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#0A2540]">
                  {i18n.language === 'fr' ? category.name_fr : category.name_en}
                </h2>
                <Badge className="bg-[#00D4FF] text-white">{products.length}</Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map(product => (
                  <Card
                    key={product.id}
                    className="hover:shadow-lg transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/rental/${product.id}`)}
                  >
                    <div className="relative">
                      <img
                        src={product.images && product.images.length > 0 ? product.images[0] : '/placeholder-equipment.jpg'}
                        alt={i18n.language === 'fr' ? product.name_fr : product.name_en}
                        className="w-full h-48 object-cover rounded-t-lg"
                      />
                      {product.stock_rental > 0 ? (
                        <Badge className="absolute top-2 right-2 bg-[#635BFF] text-white">
                          Disponible
                        </Badge>
                      ) : (
                        <Badge className="absolute top-2 right-2 bg-red-500 text-white">
                          Indisponible
                        </Badge>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg mb-2 text-[#0A2540] line-clamp-2">
                        {i18n.language === 'fr' ? product.name_fr : product.name_en}
                      </h3>
                      
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {i18n.language === 'fr' ? product.description_fr : product.description_en}
                      </p>

                      {product.location && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                          <FaMapMarkerAlt className="text-[#00D4FF]" />
                          <span>{product.location}</span>
                        </div>
                      )}

                      <div className="flex justify-between items-center">
                        <div>
                          {product.show_price !== false ? (
                            <>
                              <div className="text-2xl font-bold text-[#0A2540]">
                                {product.price_rental}€
                              </div>
                              <div className="text-sm text-gray-500">{t('rental.perDay')}</div>
                            </>
                          ) : (
                            <div className="text-lg font-semibold text-[#635BFF]">
                              {i18n.language === 'fr' ? 'Prix sur demande' : 'Price on request'}
                            </div>
                          )}
                        </div>
                        
                        <Button 
                          className="bg-[#00D4FF] hover:bg-[#00B4D8]"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/rental/${product.id}`);
                          }}
                        >
                          Voir détails
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
